import type { EventCondition } from "@packages/commons";
import { createAppResourceService } from "@/global";
import { Resource } from "@imminently/immi-query";

export interface Settings extends Resource {
  emails?: any[];
  general?: any;
  features?: any;
  connectors?: EventConnector[];
  [key: string]: any;
}

export interface EventConnector {
  id: string;
  type: "slack" | "webhook" | "email" | "teams" | "clickup";
  name: string;
  webhookUrl?: string;
  emailTo?: string[];
  emailToByEvent?: Record<string, string[]>;
  clickUpToken?: string;
  clickUpListId?: string;
  slackBotToken?: string;
  slackChannelId?: string;
  subscribedEvents?: string[];
  conditions?: EventCondition | null;
  createdAt?: string;
  updatedAt?: string;
}

// Create the base settings service
export const settingsService = createAppResourceService<Settings>("auth/meta/settings");

/**
 * Get settings for a specific tenancy
 * GET /meta/settings/{id}
 */
export const getSettings = settingsService
  .endpoint()
  .paramsOf<"id">()
  .responseOf<Settings>()
  .build({
    id: "get_settings",
    path: "/:id",
    method: "get",
  });

/**
 * Update settings for a specific tenancy
 * PATCH /meta/settings/{id}
 */
export const updateSettings = settingsService
  .endpoint()
  .paramsOf<"id">()
  .bodyOf<Partial<Settings>>()
  .responseOf<Settings>()
  .build({
    id: "update_settings",
    path: "/:id",
    method: "patch",
  });

/**
 * Create settings for a tenancy
 * POST /meta/settings
 */
export const createSettings = settingsService
  .endpoint()
  .bodyOf<Partial<Settings>>()
  .responseOf<Settings>()
  .build({
    id: "create_settings",
    path: "/",
    method: "post",
  });

/**
 * Get a file for a tenancy
 * GET /meta/settings/{id}/file/{sub_id}
 */
export const getSettingsFile = settingsService
  .endpoint()
  .paramsOf<"id" | "sub_id">()
  .responseOf<{ message: string }>()
  .build({
    id: "get_settings_file",
    path: "/:id/file/:sub_id",
    method: "get",
  });

/**
 * Update a file for a tenancy
 * PATCH /meta/settings/{id}/file/{sub_id}
 */
export const updateSettingsFile = settingsService
  .endpoint()
  .paramsOf<"id" | "sub_id">()
  .bodyOf<any>()
  .responseOf<any>()
  .build({
    id: "update_settings_file",
    path: "/:id/file/:sub_id",
    method: "patch",
  });

export const findConnectors = settingsService
  .endpoint()
  .paramsOf<"id">()
  .responseOf<{ data: EventConnector[]; total: number }>()
  .build({
    id: "find_connectors",
    path: "/:id/connector",
    method: "get",
  });

export const createConnector = settingsService
  .endpoint()
  .paramsOf<"id">()
  .bodyOf<Partial<EventConnector>>()
  .responseOf<EventConnector>()
  .build({
    id: "create_connector",
    path: "/:id/connector",
    method: "post",
  });

export const getConnector = settingsService
  .endpoint()
  .paramsOf<"id" | "sub_id">()
  .responseOf<EventConnector>()
  .build({
    id: "get_connector",
    path: "/:id/connector/:sub_id",
    method: "get",
  });

export const updateConnector = settingsService
  .endpoint()
  .paramsOf<"id" | "sub_id">()
  .bodyOf<Partial<EventConnector>>()
  .responseOf<EventConnector>()
  .build({
    id: "update_connector",
    path: "/:id/connector/:sub_id",
    method: "patch",
  });

export const deleteConnector = settingsService
  .endpoint()
  .paramsOf<"id" | "sub_id">()
  .responseOf<EventConnector>()
  .build({
    id: "delete_connector",
    path: "/:id/connector/:sub_id",
    method: "delete",
  });

export const testConnector = settingsService
  .endpoint()
  .paramsOf<"id">()
  .bodyOf<Partial<EventConnector>>()
  .responseOf<{ success: boolean; statusCode: number; message: string }>()
  .build({
    id: "test_connector",
    path: "/:id/connector/test",
    method: "post",
  });
